<template>
  <div class="sys-page">
    <div class="toolbar">
      <h3>角色管理</h3>
      <el-button v-perm="'system:role:save'" type="primary" size="small" @click="openEdit(null)">新增角色</el-button>
    </div>

    <el-table :data="roles" v-loading="loading" border stripe size="small">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="roleKey" label="角色标识" />
      <el-table-column prop="roleName" label="角色名称" />
      <el-table-column label="状态" width="100">
        <template slot-scope="s">{{ s.row.status === 1 ? "启用" : "禁用" }}</template>
      </el-table-column>
      <el-table-column label="操作" width="280">
        <template slot-scope="s">
          <el-button v-perm="'system:role:save'" size="mini" @click="openEdit(s.row)">编辑</el-button>
          <el-button v-perm="'system:role:assign'" size="mini" type="primary" plain @click="openAssign(s.row)">分配菜单</el-button>
          <el-button v-perm="'system:role:delete'" size="mini" type="danger" plain @click="removeRole(s.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog :title="editing.id ? '编辑角色' : '新增角色'" :visible.sync="editVisible" width="420px">
      <el-form :model="editing" label-width="80px" size="small">
        <el-form-item label="角色标识">
          <el-input v-model="editing.roleKey" placeholder="英文,例如 operator" />
        </el-form-item>
        <el-form-item label="角色名称">
          <el-input v-model="editing.roleName" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="editing.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="editVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="saveRole">保存</el-button>
      </div>
    </el-dialog>

    <el-dialog :title="'分配菜单 - ' + (assignTarget.roleName || '')" :visible.sync="assignVisible" width="520px">
      <el-tree
        ref="menuTree"
        :data="menuTree"
        show-checkbox
        node-key="id"
        default-expand-all
        :props="{ label: 'name', children: 'children' }" />
      <div slot="footer">
        <el-button size="small" @click="assignVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="saveAssign">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { systemModules } from "@r";
import { getBoundLeafIds, mergeCheckedIds } from "./roleMenuModel";

// Rebuild a tree from the flat menu list. Root nodes have parentId === 0.
function buildTree(flat) {
  const byId = {};
  flat.forEach(n => { byId[n.id] = { ...n, children: [] }; });
  const roots = [];
  Object.values(byId).forEach(n => {
    if (n.parentId && byId[n.parentId]) byId[n.parentId].children.push(n);
    else roots.push(n);
  });
  return roots;
}

export default {
  data() {
    return {
      loading: false,
      roles: [],
      editVisible: false,
      editing: this.blankRole(),
      assignVisible: false,
      assignTarget: {},
      menuTree: [],
      allMenusFlat: [],
    };
  },
  created() {
    this.loadRoles();
  },
  methods: {
    blankRole() { return { id: null, roleKey: "", roleName: "", status: 1 }; },
    loadRoles() {
      this.loading = true;
      systemModules.listRoles()
        .then(res => { this.roles = res.data || []; })
        .catch(e => this.$message.error(e.message || "加载失败"))
        .finally(() => { this.loading = false; });
    },
    openEdit(row) {
      this.editing = row ? { ...row } : this.blankRole();
      this.editVisible = true;
    },
    saveRole() {
      systemModules.saveRole(this.editing)
        .then(() => { this.$message.success("保存成功"); this.editVisible = false; this.loadRoles(); })
        .catch(e => this.$message.error(e.message || "保存失败"));
    },
    removeRole(row) {
      this.$confirm(`确认删除角色 ${row.roleName} ?`, "提示", { type: "warning" })
        .then(() => systemModules.deleteRole(row.id))
        .then(() => { this.$message.success("已删除"); this.loadRoles(); })
        .catch(e => e !== "cancel" && this.$message.error((e && e.message) || "删除失败"));
    },
    async openAssign(row) {
      this.assignTarget = row;
      this.assignVisible = true;
      try {
        const [menusRes, boundRes] = await Promise.all([
          systemModules.listMenus(),
          systemModules.getRoleMenus(row.id),
        ]);
        this.allMenusFlat = menusRes.data || [];
        this.menuTree = buildTree(this.allMenusFlat);
        this.$nextTick(() => {
          const bound = boundRes.data || [];
          // 只传已授权叶子节点，让 el-tree 自己恢复父节点的全选/半选状态。
          // 若直接传已保存的半选父节点，Element 会级联选中该父节点下的所有接口。
          const boundLeaves = getBoundLeafIds(this.allMenusFlat, bound);
          this.$refs.menuTree && this.$refs.menuTree.setCheckedKeys(boundLeaves, false);
        });
      } catch (e) {
        this.$message.error(e.message || "加载菜单失败");
      }
    },
    saveAssign() {
      // 半选父节点也必须授权，否则后端组树时子菜单会失去父节点并被当成顶级菜单。
      // 对话框加载时只用叶子节点回显，因此保存半选父节点不会导致下次全选其所有子项。
      const menuIds = mergeCheckedIds(
        this.$refs.menuTree.getCheckedKeys(),
        this.$refs.menuTree.getHalfCheckedKeys(),
      );
      systemModules.assignRoleMenus(this.assignTarget.id, menuIds)
        .then(() => { this.$message.success("菜单已更新"); this.assignVisible = false; })
        .catch(e => this.$message.error(e.message || "保存失败"));
    },
  },
};
</script>

<style scoped>
.sys-page { padding: 16px; background: #fff; min-height: 100%; }
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.toolbar h3 { margin: 0; }
</style>
